import { uvToSphereGlsl, applyInverseWarpGlsl } from './glslUtils';

/** Brush shader — circular dot with distance-based opacity falloff.
 *  Supports both wrap-mode (3D sphere angular distance) and
 *  projection-mode (NDC screen-space circle).
 *  Output is premultiplied RGBA accumulated into the stroke render-target. */

export const brushVertexShader = /*glsl*/`
  out vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position, 1.0);
  }
`;

export const brushFragmentShader = /*glsl*/`
  precision highp float;
  in vec2 vUv;
  out vec4 fragColor;
  uniform sampler2D uPrev;
  uniform vec3  uBrushCenter;
  uniform float uBrushRadius;
  uniform vec3  uBrushColor;
  uniform int   uProjMode;
  uniform mat4  uCamViewProj;
  uniform vec3  uCamPos;
  uniform vec2  uBrushCenterNDC;
  uniform float uViewAspect;
  uniform vec2  uSphereCenterNDC;
  uniform float uInverseWarpK;
  uniform float uInverseWarpStrength;

${uvToSphereGlsl}${applyInverseWarpGlsl}
  void main() {
    vec4 prev = texture(uPrev, vUv);
    vec3 pos = uvToSphere(vUv);

    float dist;
    if (uProjMode == 1) {
      // Projection mode: screen-space circle.
      // Reject surface points not facing this (possibly rotated) camera.
      if (dot(pos, uCamPos) <= 0.0) { fragColor = prev; return; }
      vec4 clipPos = uCamViewProj * vec4(pos, 1.0);
      if (clipPos.w <= 0.0) { fragColor = prev; return; }
      vec2 ndc = clipPos.xy / clipPos.w;
      vec2 d = ndc - uBrushCenterNDC;
      d.x *= uViewAspect; // convert x from NDC to square pixel units
      dist = length(d);
    } else if (uProjMode == 2) {
      // Inverse mode: compare NDC position of the fragment's back-hemisphere
      // hit against the back-hit center anchor supplied by the CPU.
      if (dot(pos, uCamPos) <= 0.0) { fragColor = prev; return; }
      float camDist2 = dot(uCamPos, uCamPos);
      vec3 rayFrag = pos - uCamPos;
      float tBack = (camDist2 - 1.0) / dot(rayFrag, rayFrag);
      if (tBack <= 0.0) { fragColor = prev; return; }
      vec3 pBack = uCamPos + tBack * rayFrag;
      vec4 clipPos = uCamViewProj * vec4(pBack, 1.0);
      if (clipPos.w <= 0.0) { fragColor = prev; return; }
      vec2 ndc = clipPos.xy / clipPos.w;
      vec2 ndcWarped = applyInverseWarp(ndc);
      vec2 d = ndcWarped - uBrushCenterNDC;
      d.x *= uViewAspect;
      dist = length(d);
    } else {
      dist = distance(pos, uBrushCenter);
    }

    // Sub-pixel AA: fwidth gives the SDF gradient magnitude across one texel,
    // so smoothstep over [R-grad, R] produces exactly 1-texel soft edge without
    // any visible halo — matching the quality of the envelope shader.
    float edge = fwidth(dist);
    float mask = 1.0 - smoothstep(uBrushRadius - edge, uBrushRadius, dist);

    float outAlpha = max(prev.a, mask);
    // Premultiplied alpha: store color*mask so bilinear edge filtering
    // doesn't pull toward black (premultiplied blend on the stroke mesh).
    vec3 outColor = (mask > prev.a) ? uBrushColor * mask : prev.rgb;
    fragColor = vec4(outColor, outAlpha);
  }
`;
